import { TooltipV2 } from '@cimplico/components';
import { ArrowSmallDownIcon, ArrowSmallUpIcon } from '@heroicons/react/20/solid';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Currency, { formatCurrencyAmount } from '../../components/currency/Currency';
import { getVarianceDirectionColor } from '../../utilities/variance-color';

type ProfitLossProps = {
    ledgerProfitLoss?: number;
    ledgerProfitLossComparative?: number;
    workpaperProfitLoss?: number;
};

function ProfitLoss({ ledgerProfitLoss = 0, ledgerProfitLossComparative = 0, workpaperProfitLoss = 0 }: ProfitLossProps): JSX.Element {
    const [showPercent, setShowPercent] = useState(false);

    // We need to see if this is good
    const increasingProfit = workpaperProfitLoss > ledgerProfitLossComparative;
    const numericalChange = workpaperProfitLoss - ledgerProfitLossComparative;
    // Calculate the change as a percentage
    const percentageChange = ((workpaperProfitLoss - ledgerProfitLossComparative) / ledgerProfitLossComparative) * 100;

    return (
        <div>
            <p className="text-sm text-gray-500">Net Profit/Loss</p>
            <div className="space-y-1">
                <div className="flex items-center">
                    <Currency
                        as="label"
                        value={Math.abs(workpaperProfitLoss)}
                        className="text-indigo-600 font-medium block"
                        withParentheses={(workpaperProfitLoss ?? 0) < 0}
                    />

                    {ledgerProfitLossComparative !== 0 && (
                        <>
                            <span className={`${increasingProfit ? 'text-green-600' : 'text-red-600'} ml-2`}>
                                {increasingProfit ? <ArrowSmallUpIcon className="w-4 h-4 " /> : <ArrowSmallDownIcon className="w-4 h-4" />}
                            </span>
                            <TooltipV2
                                message={`Comparative amount: ${formatCurrencyAmount({
                                    value: Math.abs(ledgerProfitLossComparative),
                                    withParentheses: ledgerProfitLossComparative < 0,
                                })}`}
                                delayMs={750}
                            >
                                <p onClick={(): void => setShowPercent(!showPercent)} className="cursor-pointer">
                                    {showPercent ? (
                                        <label className="text-gray-500 font-medium text-sm cursor-pointer">
                                            {Math.abs(percentageChange).toFixed(2)}%
                                        </label>
                                    ) : (
                                        <Currency
                                            as="label"
                                            value={Math.abs(numericalChange)}
                                            className="text-gray-500 font-medium text-sm cursor-pointer"
                                        />
                                    )}
                                </p>
                            </TooltipV2>
                        </>
                    )}
                </div>

                {/* If the ledger and workpaper amount differ show the diff */}
                {ledgerProfitLoss !== workpaperProfitLoss && (
                    <>
                        <p className="text-sm text-gray-500">
                            <Currency
                                as="label"
                                value={Math.abs(ledgerProfitLoss)}
                                className="text-gray-500 font-medium text-sm"
                                withParentheses={(ledgerProfitLoss ?? 0) < 0}
                            />{' '}
                            in the ledger
                        </p>
                        <p className="text-sm text-gray-500">
                            <Currency
                                as="label"
                                value={Math.max((workpaperProfitLoss ?? 0) - (ledgerProfitLoss ?? 0))}
                                className="text-gray-500 font-medium text-sm"
                                withParentheses={(workpaperProfitLoss ?? 0) - (ledgerProfitLoss ?? 0) < 0}
                            />{' '}
                            of{' '}
                            <Link to="../journals?tab=draft" className="underline">
                                draft journals
                            </Link>
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}

export default ProfitLoss;
